import React from 'react';

const PrintablePaymentDetails = ({ paymentData, affiliateInfo, paymentHistory }) => {
  // Helper function to get display name
  // const getDisplayName = () => {
  //   if (affiliateInfo.firstName && affiliateInfo.lastName) {
  //     return `${affiliateInfo.firstName} ${affiliateInfo.lastName}`;
  //   }
  //   return affiliateInfo.username || 'N/A';
  // };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return amount.toLocaleString('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Helper function to get payment status with color
  const getPaymentStatus = (payment) => {
    if (payment.amountUnpaid === 0) {
      return <span className="text-green-600 font-medium">Paid</span>;
    }
    if (payment.amountPaid > 0) {
      return <span className="text-yellow-600 font-medium">Partial</span>;
    }
    return <span className="text-red-600 font-medium">Unpaid</span>;
  };

  return (
    <div className="p-8 bg-white">
      {/* Header with Logo and Title */}
      <div className="flex justify-between items-start mb-8 border-b pb-4">
        <div className="flex items-center">
          <div className="text-2xl font-bold" style={{ color: '#f97316' }}>INFUBER</div>
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-bold text-gray-800">Payment Details Report</h1>
          <p className="text-gray-600 text-sm">Generated on {new Date().toLocaleDateString()}</p>
        </div>
      </div>

      {/* Affiliate Information */}
      <div className="mb-8 bg-gray-50 p-6 rounded-lg">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Affiliate Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Name</p>
              <p className="font-medium text-gray-800">
                {affiliateInfo.firstName && affiliateInfo.lastName 
                  ? `${affiliateInfo.firstName} ${affiliateInfo.lastName}`
                  : affiliateInfo.username || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Email</p>
              <p className="font-medium text-gray-800">{affiliateInfo.email || 'N/A'}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Instagram ID</p>
              <p className="font-medium text-gray-800">{affiliateInfo.instagramId || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Affiliate Type</p>
              <p className="font-medium text-gray-800 capitalize">{affiliateInfo.affiliateType || 'N/A'}</p>
            </div>
            {affiliateInfo.companyName && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Company Name</p>
                <p className="font-medium text-gray-800">{affiliateInfo.companyName}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current Payment Summary */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Current Payment Summary</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div className="border p-4 rounded-lg bg-white shadow-sm">
            <p className="text-sm text-gray-500 mb-2">Total Due Amount</p>
            <p className="font-medium text-lg text-gray-800">
              {formatCurrency(paymentData.totalDue)}
            </p>
          </div>
          <div className="border p-4 rounded-lg bg-white shadow-sm">
            <p className="text-sm text-gray-500 mb-2">Amount Paid</p>
            <p className="font-medium text-lg text-green-600">
              {formatCurrency(paymentData.amountPaid)}
            </p>
          </div>
          <div className="border p-4 rounded-lg bg-white shadow-sm">
            <p className="text-sm text-gray-500 mb-2">Amount Unpaid</p>
            <p className="font-medium text-lg text-red-600">
              {formatCurrency(paymentData.amountUnpaid)}
            </p>
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Payment History</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full border rounded-lg">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Due</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Paid</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Unpaid</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paymentHistory.map((payment, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {new Date(payment.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(payment.totalDue)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(payment.amountPaid)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(payment.amountUnpaid)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {payment.lastUpdatedBy?.name || payment.brandName || 'System'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {payment.referenceMessage || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {getPaymentStatus(payment)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            <p>© {new Date().getFullYear()} Infuber. All rights reserved.</p>
          </div>
          <div className="text-sm text-gray-500">
            <p>This is a computer-generated document.</p>
            <p>Document ID: {Math.random().toString(36).substring(2, 15)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintablePaymentDetails; 